import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/ui/Header";
import Breadcrumb from "../../components/ui/Breadcrumb";

import RoomImageGallery from "./components/RoomImageGallery";
import RoomInfo from "./components/RoomInfo";
import RoomContactForm from "./components/RoomContactForm";
import RoomBookingPanel from "./components/RoomBookingPanel";
import RelatedRooms from "./components/RelatedRooms";

import Icon from "../../components/AppIcon";

import { ROOMS } from "../../data/properties";
import {
  PRICING_BY_SLUG,
  getPriceForDate,
  buildSeasonalPricesByMonth,
} from "../../data/pricing";
import { Helmet } from "react-helmet-async";

const getTzDateParts = (timeZone = "Europe/Zagreb") => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(new Date());
  const get = (t) => Number(parts.find((p) => p.type === t)?.value);
  return { y: get("year"), m: get("month"), d: get("day") };
};

export default function RoomDetailSlug() {
  const { slug } = useParams();

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDates, setSelectedDates] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  useEffect(() => {
    setCurrentPrice(null);
  }, [slug]);

  // ✅ today (Europe/Zagreb) mora biti prije useEffect-a jer ga koristi
  const { y, m, d } = getTzDateParts("Europe/Zagreb");
  const today = useMemo(() => new Date(Date.UTC(y, m - 1, d)), [y, m, d]);

  useEffect(() => {
    setLoading(true);

    const base = ROOMS.find((r) => r.slug === slug);
    if (!base) {
      setRoom(null);
      setLoading(false);
      return;
    }

    const seasons = PRICING_BY_SLUG[base.slug] || [];
    const todayPrice = getPriceForDate(seasons, today, 2);

    setRoom({
      ...base,
      seasons,
      seasonalPrices: buildSeasonalPricesByMonth(seasons),
      price: todayPrice ?? base.price,
    });

    setLoading(false);
  }, [slug, today]);

  const handlePriceChange = (priceOrNull) => {
    if (priceOrNull == null) {
      // vrati današnju cijenu
      setRoom((prev) => {
        if (!prev) return prev;
        const seasons = PRICING_BY_SLUG[prev.slug] || [];
        const todayPrice = getPriceForDate(seasons, today, 2);
        return { ...prev, price: todayPrice ?? prev.price };
      });
      return;
    }

    setRoom((prev) => (prev ? { ...prev, price: priceOrNull } : prev));
  };

  const breadcrumbItems = [
    { label: "Home", path: "/", icon: "Home" },
    { label: "Rooms", path: "/sobe", icon: "Bed" },
    { label: room?.title || "Room Details", isActive: true, icon: "MapPin" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-96 bg-muted rounded-xl mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-8 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
              </div>
              <div className="h-64 bg-muted rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon
            name="AlertTriangle"
            size={48}
            className="text-muted-foreground mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Room Not Found
          </h1>
          <p className="text-muted-foreground">
            The room you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  const relatedRooms = ROOMS.filter((r) => r.slug !== room.slug).map((r) => ({
    id: r.slug,
    slug: r.slug,
    title: r.title,
    location: "Guest House Sonja",
    imageUrl: r.images?.[0],
    link: `/sobe/${r.slug}`,
    status: r.status,
    capacity: r.capacity,
    price: getPriceForDate(PRICING_BY_SLUG[r.slug], today, 2) ?? 0,
  }));

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{room.title} | Guest House Sonja Krk</title>
        <meta
          name="description"
          content={`Stay in ${room.title} at Guest House Sonja Krk. Comfortable room near the center of Krk with free WiFi and air conditioning.`}
        />
        <link
          rel="canonical"
          href={`https://www.guesthousesonjakrk.com/sobe/${room.slug}`}
        />
      </Helmet>

      <Header />

      <div className="container mx-auto px-6 lg:px-8 py-6">
        <Breadcrumb customItems={breadcrumbItems} />

        <RoomImageGallery images={room.images} title={room.title} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2">
            <RoomInfo room={room} starCount={3} currentPrice={currentPrice} />

            <div className="mt-12">
              <RoomContactForm room={room} prefillDates={selectedDates} />
            </div>
          </div>

          <div className="lg:col-span-1">
            <RoomBookingPanel
              room={room}
              onPriceChange={setCurrentPrice}
              onDatesChange={setSelectedDates}
            />
          </div>
        </div>

        <div className="mt-16">
          <RelatedRooms rooms={relatedRooms} />
        </div>
      </div>
    </div>
  );
}
